/**
 * ab-cart-special-offer-v1: mark qualifying paid lines for post-purchase
 * gift-card issuance and remove legacy promo gift-card lines from the cart.
 */
(function () {
  const GIFT_PROPERTY = "_special_offer_gift";
  const EARN_GIFT_CART_PROPERTY = "_earn_gift_card";
  const SYNC_SOURCE = "cart-special-offer-gift";
  const SYNC_DEBOUNCE_MS = 120;
  let syncing = false;
  let syncQueued = false;
  let syncTimer;

  function isAbActive() {
    const root = document.documentElement;
    return root.classList.contains("ab-cart-special-offer-v1");
  }

  function getConfig() {
    const el = document.getElementById("cart-special-offer-gift-config");
    if (!el) return null;

    try {
      return JSON.parse(el.textContent);
    } catch (error) {
      console.error("cart-special-offer-gift: invalid config", error);
      return null;
    }
  }

  function getThreshold(config, currency) {
    return config.thresholds[currency] ?? config.thresholds.DEFAULT ?? null;
  }

  function isEarnGiftTagged(item) {
    return item.properties?.[EARN_GIFT_CART_PROPERTY] === "true";
  }

  function getQualifyingTotal(cart, variantId) {
    return cart.items.reduce((sum, item) => {
      if (item.gift_card || item.final_line_price === 0) return sum;
      if (String(item.variant_id) === String(variantId)) return sum;
      if (item.properties?.[GIFT_PROPERTY] === "true") return sum;
      return sum + item.final_line_price;
    }, 0);
  }

  function findLegacyGiftLine(cart) {
    return cart.items.find(
      (item) => item.properties?.[GIFT_PROPERTY] === "true",
    );
  }

  function findLineToReconcile(cart, shouldEarnGift, variantId) {
    return cart.items.find((item) => {
      const isEligible =
        !item.gift_card &&
        item.final_line_price > 0 &&
        String(item.variant_id) !== String(variantId) &&
        item.properties?.[GIFT_PROPERTY] !== "true";
      const isTagged = isEarnGiftTagged(item);

      if (isEligible) {
        return shouldEarnGift ? !isTagged : isTagged;
      }

      return isTagged;
    });
  }

  /**
   * Swish auto-saves cart lines when Shopify line keys change (e.g. property
   * updates). Suppress that feedback while this script reconciles properties.
   */
  function withSwishCartSaveSuppressed(callback) {
    return window.VbysCartRetag.withSwishCartSaveSuppressed(callback);
  }

  async function fetchCart() {
    const response = await fetch(`${routes.cart_url}.js`, { cache: "no-store" });
    if (!response.ok) throw new Error("Failed to fetch cart");
    return response.json();
  }

  async function reloadCartDrawer(cart) {
    const response = await fetch(`${routes.cart_url}?section_id=cart-drawer`, {
      cache: "no-store",
    });
    if (!response.ok) throw new Error("Failed to fetch cart drawer section");

    const html = await response.text();
    const doc = new DOMParser().parseFromString(html, "text/html");
    const source = doc.querySelector("#CartDrawer .drawer__inner");
    const target = document.querySelector("#CartDrawer .drawer__inner");
    const cartDrawer = document.querySelector("cart-drawer");

    if (source && target) {
      target.innerHTML = source.innerHTML;
    }

    if (cartDrawer && cart) {
      cartDrawer.classList.toggle("is-empty", cart.item_count === 0);
    }

    const overlay = document.querySelector("#CartDrawer-Overlay");
    if (cartDrawer && overlay) {
      overlay.addEventListener("click", cartDrawer.close.bind(cartDrawer));
    }

    document.dispatchEvent(new CustomEvent("cart-upsell-slider:init"));
    document.dispatchEvent(new CustomEvent("handleSwatchHover:init"));
    document.dispatchEvent(new CustomEvent("variant-options:init"));
    document.dispatchEvent(
      new CustomEvent("cart-drawer:rendered", {
        detail: { source: SYNC_SOURCE },
      }),
    );
  }

  async function refreshCartDrawer(cart) {
    await publish(PUB_SUB_EVENTS.cartUpdate, {
      source: SYNC_SOURCE,
      cartData: cart,
    });
    await reloadCartDrawer(cart);
  }

  function handleUpsellOptionToggle(event) {
    if (!isAbActive() || !(event.target instanceof Element)) return;

    const toggle = event.target.closest("[js-product-option-modle]");
    if (toggle) {
      const option = toggle
        .closest("div, section, article")
        ?.querySelector("[js-product-option]");
      if (!option) return;

      toggle.classList.add("active");
      option.classList.add("active");
      return;
    }

    if (event.target.closest("[js-product-option]")) return;

    document
      .querySelectorAll("[js-product-option-modle].active")
      .forEach((activeToggle) => {
        activeToggle.classList.remove("active");
        activeToggle
          .closest("div, section, article")
          ?.querySelector("[js-product-option]")
          ?.classList.remove("active");
      });
  }

  async function reconcileLineProperties(line, shouldEarnGift) {
    const properties = { ...(line.properties || {}) };

    if (shouldEarnGift) {
      properties[EARN_GIFT_CART_PROPERTY] = "true";
    } else {
      delete properties[EARN_GIFT_CART_PROPERTY];
    }

    const response = await fetch(routes.cart_change_url, {
      ...fetchConfig(),
      body: JSON.stringify({
        id: line.key,
        quantity: line.quantity,
        properties,
      }),
    });

    const data = await response.json();
    if (!response.ok)
      throw new Error(
        data.description ||
          data.message ||
          "Failed to update reward eligibility",
      );
    return data;
  }

  async function removeLegacyGiftLine(giftLine) {
    const response = await fetch(routes.cart_change_url, {
      ...fetchConfig(),
      body: JSON.stringify({
        id: giftLine.key,
        quantity: 0,
      }),
    });

    const data = await response.json();
    if (!response.ok)
      throw new Error(
        data.description || data.message || "Failed to remove legacy gift",
      );
    return data;
  }

  async function syncSpecialOfferGift() {
    if (syncing) {
      syncQueued = true;
      return;
    }

    const config = getConfig();
    if (!config?.variantId) return;

    syncing = true;

    try {
      await withSwishCartSaveSuppressed(async () => {
        let cart = await fetchCart();
        let remainingChanges = cart.items.length * 2;
        let legacyGiftRemoved = false;
        let propertiesChanged = false;

        while (remainingChanges > 0) {
          const legacyGiftLine = findLegacyGiftLine(cart);
          if (!legacyGiftLine) break;

          cart = await removeLegacyGiftLine(legacyGiftLine);
          remainingChanges -= 1;
          legacyGiftRemoved = true;
        }

        const abActive = isAbActive();
        const threshold = getThreshold(config, cart.currency);
        const qualifyingTotal = getQualifyingTotal(cart, config.variantId);
        const shouldEarnGift =
          abActive && threshold != null && qualifyingTotal >= threshold;

        while (remainingChanges > 0) {
          const line = findLineToReconcile(
            cart,
            shouldEarnGift,
            config.variantId,
          );
          if (!line) break;

          cart = await reconcileLineProperties(line, shouldEarnGift);
          remainingChanges -= 1;
          propertiesChanged = true;
        }

        const reconciliationPending =
          Boolean(findLegacyGiftLine(cart)) ||
          Boolean(findLineToReconcile(cart, shouldEarnGift, config.variantId));

        if (reconciliationPending) syncQueued = true;

        if (legacyGiftRemoved && !reconciliationPending) {
          await refreshCartDrawer(cart);
        } else if (propertiesChanged && !reconciliationPending) {
          await publish(PUB_SUB_EVENTS.cartUpdate, {
            source: SYNC_SOURCE,
            cartData: cart,
          });
        }
      });
    } catch (error) {
      console.error("cart-special-offer-gift:", error);
    } finally {
      syncing = false;
      if (syncQueued) {
        syncQueued = false;
        syncSpecialOfferGift();
      }
    }
  }

  function scheduleSyncSpecialOfferGift() {
    clearTimeout(syncTimer);
    syncTimer = setTimeout(syncSpecialOfferGift, SYNC_DEBOUNCE_MS);
  }

  function initSpecialOfferGiftSync() {
    document.addEventListener("click", handleUpsellOptionToggle);

    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === SYNC_SOURCE) return;
      scheduleSyncSpecialOfferGift();
    });

    document.addEventListener("cart-drawer:rendered", (event) => {
      if (event.detail?.source === SYNC_SOURCE) return;
      scheduleSyncSpecialOfferGift();
    });

    const root = document.documentElement;
    let wasActive = isAbActive();
    const observer = new MutationObserver(() => {
      const isActive = isAbActive();
      if (isActive !== wasActive) {
        wasActive = isActive;
        scheduleSyncSpecialOfferGift();
      }
    });
    observer.observe(root, { attributes: true, attributeFilter: ["class"] });

    scheduleSyncSpecialOfferGift();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initSpecialOfferGiftSync);
  } else {
    initSpecialOfferGiftSync();
  }
})();
